import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode
} from 'react';
import authService from '../services/auth.service';
import { User, LoginRequest } from '../types';

// â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
// Auth Context Shape
// Everything any component might need from auth state
// â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
interface AuthContextType {
  user:            User | null;     // Current logged-in user (null if not logged in)
  isLoading:       boolean;         // TRUE while verifying token on app load
  isAuthenticated: boolean;         // TRUE if user is logged in
  login:           (credentials: LoginRequest) => Promise<{
    must_change_pwd: boolean;
  }>;
  logout:          () => Promise<void>;
  updateUser:      (user: User) => void;  // Update local user state after profile edit
  hasRole:         (...roles: string[]) => boolean; // Role check helper
  canAccess:       (module: string, action: 'read' | 'write' | 'delete' | 'approve' | 'export') => boolean;
}

// â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
// Create Context
// Default is null â€” will throw if useAuth is used outside provider
// â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
const AuthContext = createContext<AuthContextType | null>(null);

// â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
// Auth Provider
// Wraps the entire app in App.tsx
// Manages auth state â€” all components consume via useAuth()
// â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user,      setUser]      = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // â”€â”€ Verify token on app load â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  // When user refreshes the page, check if stored token is still valid
  // If backend rejects (revoked/expired), clear local state
  useEffect(() => {
    const verifyToken = async () => {
      const token = authService.getToken();

      if (!token) {
        setIsLoading(false);
        return;
      }

      try {
        // Call GET /api/auth/me â€” backend verifies JWT + checks session table
        console.log('verifyToken calling /auth/me'); const currentUser = await authService.getCurrentUser(); console.log('verifyToken success:', currentUser);
        setUser(currentUser);

        // Update localStorage with fresh user data
        localStorage.setItem('rbpms_user', JSON.stringify(currentUser));
      } catch (err) { console.error("verifyToken failed:", err);
        // Token invalid or session revoked â€” clear everything
        localStorage.removeItem('rbpms_token');
        localStorage.removeItem('rbpms_user');
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    };

    verifyToken();
  }, []);

  // â”€â”€ Login â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  const login = useCallback(async (credentials: LoginRequest) => {
    const response = await authService.login(credentials);
    setUser(response.user);
    return { must_change_pwd: response.user.must_change_pwd };
  }, []);

  // â”€â”€ Logout â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  const logout = useCallback(async () => {
    await authService.logout();
    setUser(null);
  }, []);

  // â”€â”€ Update user locally â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  // Called after profile edit â€” updates context without re-login
  const updateUser = useCallback((updatedUser: User) => {
    setUser(updatedUser);
    localStorage.setItem('rbpms_user', JSON.stringify(updatedUser));
  }, []);

  // â”€â”€ Role check helper â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  // Usage: hasRole('ADMIN', 'MD') â†’ true if current user is ADMIN or MD
  // Used in components to show/hide buttons and sections
  const hasRole = useCallback((...roles: string[]): boolean => {
    if (!user) return false;
    return roles.includes(user.role_code);
  }, [user]);

  // â”€â”€ Module access check â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  // Basic check based on role â€” full check is done by backend
  // Frontend uses this to show/hide UI elements only
  // Backend always enforces real permissions â€” frontend is UI only
  const canAccess = useCallback((
    module: string,
    action: 'read' | 'write' | 'delete' | 'approve' | 'export'
  ): boolean => {
    if (!user) return false;
    // ADMIN has access to everything
    if (user.role_code === 'ADMIN') return true;
    // MD can read and approve everything
    if (user.role_code === 'MD') {
      return action === 'read' || action === 'approve' || action === 'export';
    }
    // For other roles â€” basic check, full enforcement by backend
    return action === 'read';
  }, [user]);

  return (
    <AuthContext.Provider value={{
      user,
      isLoading,
      isAuthenticated: !!user,
      login,
      logout,
      updateUser,
      hasRole,
      canAccess,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

// â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
// useAuth Hook
// Called by any component that needs auth state
// Usage: const { user, hasRole, logout } = useAuth();
// â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used inside AuthProvider');
  }
  return context;
};

export default useAuth;

